import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const LightboxContext = createContext(() => {});

const OVERLAY_MOTION = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.25 },
};

const IMAGE_MOTION = {
  initial: { scale: 0.92, opacity: 0 },
  animate: { scale: 1, opacity: 1 },
  exit: { scale: 0.95, opacity: 0 },
  transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
};

export const useLightbox = () => useContext(LightboxContext);

export const LightboxProvider = ({ children }) => {
  const [img, setImg] = useState(null);
  const open = useCallback((src, alt = "") => setImg({ src, alt }), []);
  const close = useCallback(() => setImg(null), []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  useEffect(() => {
    document.body.style.overflow = img ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [img]);

  return (
    <LightboxContext.Provider value={open}>
      {children}
      <AnimatePresence>
        {img && (
          <motion.div
            data-testid="lightbox-overlay"
            {...OVERLAY_MOTION}
            onClick={close}
            className="fixed inset-0 z-[200] flex items-center justify-center bg-[#0F1A33]/92 backdrop-blur-md p-4 md:p-12 cursor-zoom-out"
          >
            <button
              data-testid="lightbox-close"
              aria-label="Chiudi"
              onClick={close}
              className="absolute top-5 right-5 z-10 h-11 w-11 rounded-full border border-[#F9F8F6]/30 text-[#F9F8F6] flex items-center justify-center hover:bg-[#F9F8F6] hover:text-[#0F1A33] transition-colors duration-300"
            >
              <X size={18} />
            </button>
            <motion.img
              data-testid="lightbox-image"
              src={img.src}
              alt={img.alt}
              {...IMAGE_MOTION}
              onClick={(e) => e.stopPropagation()}
              className="max-h-full max-w-full object-contain rounded-2xl shadow-2xl cursor-default"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </LightboxContext.Provider>
  );
};
